import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './CreatePostPage.module.css';

function EditPostPage() {
  const { postId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      if (!currentUser || !postId) {
        navigate('/');
        return;
      }

      try {
        const postDoc = await getDoc(doc(db, 'posts', postId));
        if (postDoc.exists()) {
          const postData = postDoc.data();
          if (postData.userId !== currentUser.uid) {
            navigate('/profile/posts');
            return;
          }
          setText(postData.text || '');
          setExistingImages(postData.images || []);
        } else {
          navigate('/profile/posts');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching post:', error);
        navigate('/profile/posts');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchPost();
  }, [currentUser, postId, navigate]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const maxImages = 2 - existingImages.length;
    const filesToAdd = files.slice(0, maxImages);
    
    setNewImageFiles(filesToAdd);
    const previews = filesToAdd.map((file) => URL.createObjectURL(file));
    setNewImagePreviews(previews);
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      navigate('/');
      return;
    }

    if (!text.trim() && existingImages.length === 0 && newImageFiles.length === 0) {
      return;
    }

    setLoading(true);
    try {
      let newImageUrls = [];
      if (newImageFiles.length > 0) {
        newImageUrls = await Promise.all(
          newImageFiles.map(async (file) => {
            const imageRef = ref(storage, `posts/${currentUser.uid}/${Date.now()}_${file.name}`);
            await uploadBytes(imageRef, file);
            return getDownloadURL(imageRef);
          }),
        );
      }

      const allImages = [...existingImages, ...newImageUrls];

      await updateDoc(doc(db, 'posts', postId), {
        text: text.trim(),
        images: allImages,
        updatedAt: serverTimestamp(),
      });

      navigate('/profile/posts');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating post:', error);
      alert('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>Chargement...</div>
        </div>
      </div>
    );
  }

  const totalImages = existingImages.length + newImageFiles.length;
  const canAddImages = totalImages < 2;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1>Modifier ma question</h1>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="text">Votre question (max 300 caractères)</label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={300}
              rows={6}
              className={styles.textarea}
              placeholder="Posez votre question à la communauté..."
            />
            <div className={styles.charCount}>{text.length}/300</div>
          </div>

          <div className={styles.formGroup}>
            <label>Images existantes</label>
            {existingImages.length > 0 && (
              <div className={styles.imagePreviews}>
                {existingImages.map((url, index) => (
                  <div key={index} className={styles.imagePreview}>
                    <img src={url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className={styles.removeImage}
                      onClick={() => handleRemoveExistingImage(index)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {canAddImages && (
            <div className={styles.formGroup}>
              <label htmlFor="images">Ajouter des images ({2 - totalImages} restantes)</label>
              <input
                id="images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className={styles.fileInput}
              />
            </div>
          )}

          {newImagePreviews.length > 0 && (
            <div className={styles.imagePreviews}>
              {newImagePreviews.map((preview, index) => (
                <div key={index} className={styles.imagePreview}>
                  <img src={preview} alt={`New preview ${index + 1}`} />
                  <button
                    type="button"
                    className={styles.removeImage}
                    onClick={() => handleRemoveNewImage(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => navigate('/profile/posts')}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading || (!text.trim() && totalImages === 0)}
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditPostPage;

